import type { PatientTreatmentPlanStageLog } from "../../../../../types/healthcare/TreatmentPlanTypes";
import type { CoaAnalysis } from "../CoaAnalysisPanel";

type WatcherItemLike = { watcher_type?: string };

/**
 * True when the active watcher is the clinical CoA-analysis watcher (coa_analysis_complete).
 * It can be configured single-type or as a multi-item watcher with one such item.
 */
export function isCoaAnalysisWatcher(log: PatientTreatmentPlanStageLog): boolean {
	const watcher = (log as any).watcher;
	const items = (watcher?.watcher_items ?? []) as WatcherItemLike[];
	return watcher?.watcher_type === "coa_analysis_complete"
		|| items.some((i) => i.watcher_type === "coa_analysis_complete");
}

/**
 * Selects the stage-log carrying the CoA analysis: the CoA UPLOAD stage-log (not the watcher —
 * AnalyseCoaJob stores the result there), among those with an `ai_coa_analysis`, with the
 * highest id (most recent).
 *
 * When `currentIteration` is supplied (per-loop-attempt rendering, F2), only logs from that exact
 * loop_iteration are considered before the highest-id reduce — so a fresh attempt's pending upload
 * is scoped away from a prior attempt's analysed result. When omitted, behaviour is the latest
 * across all logs.
 *
 * This is the ONE place the selection rule lives — `latestCoaAnalysis` below and
 * `InlineStageRenderer.tsx`'s file-id lookup (which must read off the SAME log as the analysis)
 * both delegate here rather than re-deriving the rule.
 */
export function latestCoaLog(
	stageLogs: PatientTreatmentPlanStageLog[],
	currentIteration?: number,
): PatientTreatmentPlanStageLog | undefined {
	const scoped =
		currentIteration === undefined
			? stageLogs
			: stageLogs.filter((l) => (l.loop_iteration ?? 0) === currentIteration);
	const withAnalysis = scoped.filter((l) => (l.outcome_data as any)?.ai_coa_analysis);
	if (withAnalysis.length === 0) return undefined;
	return withAnalysis.reduce((a, b) => (b.id > a.id ? b : a));
}

/**
 * The most recent CoA analysis result on the plan — see `latestCoaLog` for the selection rule.
 */
export function latestCoaAnalysis(
	stageLogs: PatientTreatmentPlanStageLog[],
	currentIteration?: number,
): CoaAnalysis | undefined {
	const log = latestCoaLog(stageLogs, currentIteration);
	return (log?.outcome_data as any)?.ai_coa_analysis as CoaAnalysis | undefined;
}
